import React, { useCallback, useEffect, useRef, useState } from "react";
import type { CalendarItem, ReviewItem, Task } from "@nelurio/contracts";
import { Card, EmptyState, PageHeader } from "@nelurio/ui";
import { useI18n } from "@nelurio/i18n";
import { CalendarMonth, localDateKey } from "../components/CalendarMonth";
import { TodayPlan } from "../components/TodayPlan";
import { api, isDemoMode } from "../api/client";
import { fetchRoutines, type Routine } from "../lib/routines";
import { useEffect as useEff } from "react";

export function PlannerPage() {
  const { t, formatDate, formatNumber } = useI18n();
  const [calendar, setCalendar] = useState<CalendarItem[]>([]);
  const [selected, setSelected] = useState(() => localDateKey(new Date()));
  const [scheduled, setScheduled] = useState<Task[]>([]);
  const [anytime, setAnytime] = useState<Task[]>([]);
  const [reviews, setReviews] = useState<ReviewItem[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [serverRoutines, setServerRoutines] = useState<Routine[]>([]);
  useEffect(() => {
    if (isDemoMode()) return;
    fetchRoutines().then((data) => setServerRoutines(data.routines)).catch(() => undefined);
  }, []);

  const loadRange = useCallback(async (from: string, to: string) => {
    // Routine dots are client-side: map each routine's weekdays onto the range.
    const routineItems = (): CalendarItem[] => {
      const out: CalendarItem[] = [];
      const cursor = new Date(`${from}T12:00:00`);
      const end = new Date(`${to}T12:00:00`);
      const routines: Routine[] = serverRoutines;
      while (cursor <= end) {
        // localDateKey, not toISOString — UTC shifts noon to the previous day in
        // UTC+13ish timezones and put routine dots on the wrong calendar cell.
        for (const routine of routines.filter((item) => item.days.includes(cursor.getDay()))) {
          const dayKey = localDateKey(cursor);
          out.push({ id: `rt-${routine.id}-${dayKey}`, date: dayKey, time: routine.time ?? undefined, title: routine.title, type: "study", courseTitle: undefined } as CalendarItem);
        }
        cursor.setDate(cursor.getDate() + 1);
      }
      return out;
    };
    if (isDemoMode()) { setCalendar(routineItems()); return; }
    try {
      const items = await api<CalendarItem[]>(`/calendar?from=${from}&to=${to}`);
      setCalendar([...(Array.isArray(items) ? items : []), ...routineItems()]);
    } catch {
      setCalendar(routineItems());
    }
  }, [serverRoutines]);

  // Day-request token: a slow older response must never overwrite a newer day
  // (same race class the DashboardPage fix covered).
  const dayRequestRef = useRef("");
  const loadDay = useCallback(async (day: string) => {
    if (isDemoMode()) return;
    dayRequestRef.current = day;
    try {
      const data = await api<{ scheduled: Task[]; anytime: Task[]; reviews: ReviewItem[] }>(`/dashboard?date=${day}`);
      if (dayRequestRef.current !== day) return;
      setScheduled(Array.isArray(data.scheduled) ? data.scheduled : []);
      setAnytime(Array.isArray(data.anytime) ? data.anytime : []);
      setReviews(Array.isArray(data.reviews) ? data.reviews : []);
    } catch {
      if (dayRequestRef.current === day) {
        setScheduled([]);
        setAnytime([]);
      }
    }
  }, []);

  useEffect(() => { void loadDay(selected).finally(() => setLoaded(true)); }, [selected, loadDay]);

  // Real upcoming strip: next 7 days counted from the loaded calendar items
  const upcoming = (() => {
    const days: Array<{ key: string; label: string; count: number }> = [];
    const base = new Date();
    for (let offset = 0; offset < 7; offset += 1) {
      const date = new Date(base.getFullYear(), base.getMonth(), base.getDate() + offset);
      const key = localDateKey(date);
      days.push({
        key,
        label: formatDate(`${key}T12:00:00`, { weekday: "short", day: "numeric" }),
        count: calendar.filter((item) => item.date === key).length
      });
    }
    return days;
  })();

  return (
    <div className="page-stack rise">
      <PageHeader title={t("nav.planner")} subtitle={t("planner.subtitle")} />
      <div className="planner-grid">
        <CalendarMonth items={calendar} selected={selected} onSelect={setSelected} onRangeChange={loadRange} />
        <div className="planner-side">
          <TodayPlan scheduled={scheduled} anytime={anytime} reviews={reviews} date={selected} />
          {loaded && !scheduled.length && !anytime.length && !reviews.length && (
            <Card><EmptyState icon="calendar" title={t("planner.nothingForDay", { day: formatDate(`${selected}T12:00:00`, { weekday: "long", day: "numeric", month: "long" }) })} description={t("planner.nothingHint")} /></Card>
          )}
        </div>
      </div>
      {calendar.length > 0 && (
        <Card className="planner-week tilt-card">
          <h2>{t("dashboard.upcoming")}</h2>
          <div className="week-timeline">
            {upcoming.map((day, index) => (
              <button
                key={day.key}
                type="button"
                className={`week-day ${index === 0 ? "is-today" : ""} ${day.key === selected ? "is-selected" : ""}`}
                onClick={() => { setSelected(day.key); window.scrollTo({ top: 0, behavior: "smooth" }); }}
                aria-label={t("tasks.pickDay")}
              >
                <strong>{day.label}</strong>
                <span>{t(day.count === 1 ? "planner.activityOne" : "planner.activities", { count: formatNumber(day.count) })}</span>
                <i style={{ height: `${Math.min(48, 8 + day.count * 10)}px` }} />
              </button>
            ))}
          </div>
        </Card>
      )}
    </div>
  );
}
